import { useEffect } from 'react';
import { Onboarding, type OnboardingProps } from '@cx-web/feature-onboarding';
import { ClientOnly } from 'remix-utils/client-only';

import { type MatchesCouponIndexProps } from '@cx-modules/matches-coupon';
import { type MoreArticlesIndexProps } from '@cx-modules/more-articles';
import { type MostPopularBetsIndexProps } from '@cx-modules/most-popular-bets';
import { type OffersListIndexProps } from '@cx-modules/offers-list';
import { type OutrightsCouponIndexProps } from '@cx-modules/outrights-coupon';
import { type TextBlockIndexProps } from '@cx-modules/text-block';

import {
  Faq,
  IconListSection,
  MatchesCoupon,
  MoreArticles,
  MostPopularBets,
  OffersCarousel,
  OffersCarouselContentContractForPageTemplate,
  OffersGrouped,
  OffersGroupedContentContractForPageTemplate,
  OffersList,
  OffersSlim,
  OffersSlimContentContractForPageTemplate,
  OutrightsCoupon,
  PremiumSignup,
  type PremiumSignupProps,
  ReviewsSection,
  SubeventHeader,
  TextBlock,
} from '~/modules';
import { getOffersClickoutServiceProps } from '~/modules/offers-clickout';
import ArticleFooter from '~/routes/$/components/ArticleFooter';
import ArticleHeader, { type ArticleHeaderProps } from '~/routes/$/components/ArticleHeader';
import BannersCarousel from '~/routes/$/components/BannersCarousel';
import Breadcrumbs from '~/routes/$/components/Breadcrumbs';
import { GridGroup } from '~/routes/$/components/GridGroup/GridGroup';
import InlineHtml from '~/routes/$/components/InlineHtml';
import { LandingPageFooter } from '~/routes/$/components/LandingPageFooter/LandingPageFooter';
import { LandingPageHeader } from '~/routes/$/components/LandingPageHeader/LandingPageHeader';
import ParentLinkHeader, { type ParentLinkHeaderProps } from '~/routes/$/components/ParentLinkHeader';
import OCConnectWidget, { type OCConnectWidgetProps } from '~/routes/$/components/Widgets/OCConnect';
import { OffersWidget, type OffersWidgetProps } from '~/routes/$/components/Widgets/Offers';
import { getSubeventId } from '~/routes/$/utils';
import { type ComponentTypeNames } from '~/types/template';
import { getPathExcludingRepubFromUrl } from '~/utils/getPathExcludingRepubFromUrl';

import { type ClientComponentMap, componentTypeNames } from './component-map.types';
import { ocConnectWidgetScript, ocOffersWidgetScript } from './scripts';

/**
 * Component types that represent groups of components
 * Used by isGroupComponent utility
 */
export const groupComponents: Partial<Record<keyof ComponentTypeNames, true>> = {
  GRID_GROUP: true,
};

/**
 * Re-export component type names for convenience
 */
export { componentTypeNames };

/**
 * Client-side component map containing React components for all dynamic page template types.
 * These components receive hydrated data from the server loader functions.
 */
export const componentMapClient: ClientComponentMap = {
  ARTICLES_WIDGET: {
    name: 'ARTICLES_WIDGET',
    Component: (props: MoreArticlesIndexProps) => {
      // `articlesPage` is intentionally SSR-only: it is derived from the request URL in the
      // server loader and arrives as a prop. Pagination is driven by full-page navigation,
      // so the pageshow handler below reloads on back/forward to keep the prop in sync.
      useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
          if (event.persisted) {
            window.location.reload();
          }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
      }, []);

      return <MoreArticles {...props} />;
    },
  },
  BANNERS_CAROUSEL: {
    name: 'BANNERS_CAROUSEL',
    Component: props => <BannersCarousel {...props} />,
  },
  BREADCRUMBS: {
    name: 'BREADCRUMBS',
    Component: props => <Breadcrumbs {...props} />,
  },
  EVERGREEN_TEXT_BLOCK: {
    name: 'EVERGREEN_TEXT_BLOCK',
    Component: (props: TextBlockIndexProps) => <TextBlock {...props} />,
  },
  GRID_GROUP: {
    name: 'GRID_GROUP',
    Component: props => <GridGroup {...props} />,
  },
  ICON_LIST: {
    name: 'ICON_LIST',
    Component: props => <IconListSection {...props} />,
  },
  LANDING_PAGE_FOOTER: {
    name: 'LANDING_PAGE_FOOTER',
    Component: props => <LandingPageFooter {...props} />,
  },
  LANDING_PAGE_HEADER: {
    name: 'LANDING_PAGE_HEADER',
    Component: props => <LandingPageHeader {...props} />,
  },
  INLINE_HTML: {
    name: 'INLINE_HTML',
    Component: props => <InlineHtml {...props} />,
  },
  ARTICLE_FOOTER_V1: {
    name: 'ARTICLE_FOOTER_V1',
    Component: props => <ArticleFooter {...props} />,
  },
  ARTICLE_HEADER_V1: {
    name: 'ARTICLE_HEADER_V1',
    Component: (props: ArticleHeaderProps) => <ArticleHeader {...props} />,
  },
  MATCHES_COUPON: {
    name: 'MATCHES_COUPON',
    Component: (props: MatchesCouponIndexProps) => <MatchesCoupon {...props} />,
  },
  MOST_POPULAR_BETS: {
    name: 'MOST_POPULAR_BETS',
    Component: (props: MostPopularBetsIndexProps) => <MostPopularBets {...props} />,
  },
  OC_CONNECT_WIDGET: {
    name: 'OC_CONNECT_WIDGET',
    Component: (props: OCConnectWidgetProps) => <ClientOnly>{() => <OCConnectWidget {...props} />}</ClientOnly>,
    scripts: [ocConnectWidgetScript],
  },
  OFFERS_WIDGET: {
    name: 'OFFERS_WIDGET',
    Component: (props: OffersWidgetProps) => <ClientOnly>{() => <OffersWidget {...props} />}</ClientOnly>,
    scripts: [ocOffersWidgetScript],
  },
  OFFERS_PAGE: {
    name: 'OFFERS_PAGE',
    Component: ({ apiKey, deviceType, nocache, requestUrl, subdivision, ...props }) => {
      const offersListProps: OffersListIndexProps = {
        ...props,
        apiKey,
        deviceTypeForApi: deviceType,
        path: getPathExcludingRepubFromUrl(props.repub, requestUrl),
        subdivisionCode: subdivision,
        nocache,
        geoSubdivisionCode: props.geoLocationInfo.geoSubdivisionCode,
        // Spread the clickout contract LAST so its keys (e.g. fullPageUrl, deviceType) always win.
        // Keeping it at the end removes the ordering trap: adding/moving keys above can no longer
        // silently shadow a clickout prop (NGF-6268 review).
        ...getOffersClickoutServiceProps({
          clickoutUrl: props.clickoutUrl,
          currentPathWithRepub: props.currentPathWithRepub,
          deviceType,
          fullPageUrl: requestUrl,
          gtmId: props.gtmId,
        }),
      };

      return (
        <div className="ogm-offers-list-wrapper">
          <div className="ogm-offers-list-content">
            {/* NGF-6300: bare clickout URL override sourced from props.clickoutUrl (was ENV.CLICKOUT_URL
                in the wrapper). Applied LAST so the bare URL wins over the clickout contract's
                `${CLICKOUT_URL}/offer/clickout` value and over the CMS componentArguments. */}
            <OffersList {...offersListProps} clickoutServiceUrl={props.clickoutUrl} />
          </div>
        </div>
      );
    },
  },
  OFFERS_CAROUSEL: {
    name: 'OFFERS_CAROUSEL',
    // The content contract spreads `...props`, so `nocache` (server-derived, from
    // ComponentLoaderContext) is already carried through — no explicit `nocache` prop needed.
    Component: props => (
      <OffersCarousel
        {...(OffersCarouselContentContractForPageTemplate(props) as unknown as React.ComponentProps<
          typeof OffersCarousel
        >)}
        {...getOffersClickoutServiceProps({
          clickoutUrl: props.clickoutUrl,
          currentPathWithRepub: props.currentPathWithRepub,
          deviceType: props.deviceType,
          fullPageUrl: props.requestUrl,
          gtmId: props.gtmId,
        })}
      />
    ),
  },
  OFFERS_GROUPED: {
    name: 'OFFERS_GROUPED',
    Component: props => (
      <OffersGrouped
        {...(OffersGroupedContentContractForPageTemplate(props) as unknown as React.ComponentProps<
          typeof OffersGrouped
        >)}
        {...getOffersClickoutServiceProps({
          clickoutUrl: props.clickoutUrl,
          currentPathWithRepub: props.currentPathWithRepub,
          deviceType: props.deviceType,
          fullPageUrl: props.requestUrl,
          gtmId: props.gtmId,
        })}
      />
    ),
  },
  OFFERS_SLIM: {
    name: 'OFFERS_SLIM',
    Component: props => (
      <OffersSlim
        {...(OffersSlimContentContractForPageTemplate(props) as unknown as React.ComponentProps<typeof OffersSlim>)}
        {...getOffersClickoutServiceProps({
          clickoutUrl: props.clickoutUrl,
          currentPathWithRepub: props.currentPathWithRepub,
          deviceType: props.deviceType,
          fullPageUrl: props.requestUrl,
          gtmId: props.gtmId,
        })}
      />
    ),
  },
  ONBOARDING_WIDGET: {
    name: 'ONBOARDING_WIDGET',
    // NGF-6479: the shared Onboarding component (@cx-web/feature-onboarding) is self-contained — the
    // CTA opens the MyOC auth modal through the CX runtime adapter, so there is no fetcher to inject.
    Component: (props: OnboardingProps) => <Onboarding {...props} />,
  },
  OUTRIGHTS_COUPON: {
    name: 'OUTRIGHTS_COUPON',
    Component: (props: OutrightsCouponIndexProps) => <OutrightsCoupon {...props} />,
  },
  PARENT_LINK_HEADER: {
    name: 'PARENT_LINK_HEADER',
    Component: (props: ParentLinkHeaderProps) => <ParentLinkHeader {...props} />,
  },
  REVIEWS_SECTION: {
    name: 'REVIEWS_SECTION',
    Component: props => <ReviewsSection {...props} />,
  },
  SECTION_FAQ: {
    name: 'SECTION_FAQ',
    Component: props => <Faq {...props} />,
  },
  PREMIUM_SIGNUP: {
    name: 'PREMIUM_SIGNUP',
    Component: (props: PremiumSignupProps) => <PremiumSignup {...props} />,
  },
  SUBEVENT_HEADER: {
    name: 'SUBEVENT_HEADER',
    Component: props => {
      const subeventId = getSubeventId(props.subeventMeta);
      return <SubeventHeader {...props} subeventId={subeventId} />;
    },
  },
} satisfies ClientComponentMap;
